import { type WorkCalendar, requireIsoDate, workingDaysInclusive } from '@pst/core-scheduling';
import type { GroupView, SchedDependency, SchedTask, ScheduleView } from '../../api';

/** Id of the bar that stands for a summary on the simplified plan. */
export const SUMMARY_PREFIX = 'summary:';
export const isSummaryRow = (id: string): boolean => id.startsWith(SUMMARY_PREFIX);
export const summaryIdOf = (id: string): string => id.slice(SUMMARY_PREFIX.length);

const byOutline = (a: SchedTask, b: SchedTask): number => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'de', { numeric: true });

/**
 * The simplified plan: every summary becomes one bar from the earliest start
 * to the latest finish of its members, everything else stays as it is.
 * Pure - the detailed plan is still the one that is calculated; this only
 * condenses what the calculation produced.
 *
 * - The bar sits where its first member sits (phase and order); it is
 *   critical when a member is, done when all are, running when some are.
 * - Relationships between members disappear, those from and to members go
 *   from and to the bar, doubles once.
 * - Phases that have nothing left to show are left out.
 */
export function condense(
  schedule: ScheduleView,
  dependencies: readonly SchedDependency[],
  calendar: WorkCalendar,
): { schedule: ScheduleView; dependencies: SchedDependency[] } {
  const summaries = schedule.summaries ?? [];
  const known = new Set(summaries.map((s) => s.id));
  const members = new Map<string, SchedTask[]>();
  for (const t of schedule.tasks) {
    if (t.summaryId && known.has(t.summaryId)) members.set(t.summaryId, [...(members.get(t.summaryId) ?? []), t]);
  }
  const replaced = new Map<string, string>();
  const bars: SchedTask[] = [];
  for (const s of summaries) {
    const list = (members.get(s.id) ?? []).sort(byOutline);
    if (list.length === 0) continue;
    const id = SUMMARY_PREFIX + s.id;
    for (const t of list) replaced.set(t.id, id);
    const starts = list.map((t) => t.earlyStart).filter((d): d is string => !!d).sort();
    const finishes = list.map((t) => t.earlyFinish).filter((d): d is string => !!d).sort();
    const start = starts[0] ?? null;
    const finish = finishes[finishes.length - 1] ?? null;
    const first = list[0]!;
    const last = list[list.length - 1]!;
    const owners = [...new Set(list.map((t) => t.owner).filter((o): o is string => !!o))];
    const floats = list.map((t) => t.totalFloat).filter((f): f is number => f !== null);
    const allMilestones = list.every((t) => t.durationDays === 0);
    const status = list.every((t) => t.status === 'done') ? 'done'
      : list.some((t) => t.status === 'blocked') ? 'blocked'
      : list.some((t) => t.status === 'running' || t.status === 'done') ? 'running' : 'open';
    bars.push({
      ...first,
      id,
      code: list.length > 1 ? `${first.code}–${last.code}` : first.code,
      name: s.name,
      durationDays: allMilestones || !start || !finish ? 0 : workingDaysInclusive(calendar, requireIsoDate(start), requireIsoDate(finish)),
      note: `${list.length} Vorgänge: ${list.map((t) => t.code).join(', ')}`,
      status,
      owner: owners.length ? owners.join('/') : null,
      deliverable: null,
      deadline: list.map((t) => t.deadline).filter((d): d is string => !!d).sort()[0] ?? null,
      constraintType: null,
      constraintDate: null,
      earlyStart: start,
      earlyFinish: finish,
      lateStart: null,
      lateFinish: null,
      totalFloat: floats.length ? Math.min(...floats) : null,
      freeFloat: null,
      critical: list.some((t) => t.critical === true),
      summaryId: s.id,
    });
  }
  const tasks = [...schedule.tasks.filter((t) => !replaced.has(t.id)), ...bars];

  const seen = new Set<string>();
  const deps: SchedDependency[] = [];
  for (const d of dependencies) {
    const from = replaced.get(d.predecessorId) ?? d.predecessorId;
    const to = replaced.get(d.successorId) ?? d.successorId;
    if (from === to) continue;
    const key = `${from}|${to}|${d.type}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deps.push({ ...d, id: from === d.predecessorId && to === d.successorId ? d.id : `${d.id}:${key}`, predecessorId: from, successorId: to });
  }

  // Only phases that still hold something, directly or below.
  const parent = new Map(schedule.groups.map((g) => [g.id, g.parentId]));
  const used = new Set<string>();
  for (const t of tasks) {
    let g = t.wbsNodeId;
    let guard = 0;
    while (g && guard++ < 100) { used.add(g); g = parent.get(g) ?? null; }
  }
  const groups: GroupView[] = schedule.groups.filter((g) => used.has(g.id));

  return { schedule: { ...schedule, tasks, groups }, dependencies: deps };
}
